import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/* ================= FIELD CATALOG ================= */

export interface ViewLeadFieldDef {
  key: string;
  label: string;
}

export interface ViewLeadFieldGroup {
  /** Section-level toggle key — unticking hides the whole card/section. */
  key: string;
  label: string;
  /** Per-field toggles inside the section (empty = section toggle only). */
  fields: ViewLeadFieldDef[];
  /**
   * Section renders as a collapsible card, so it also gets an
   * "open expanded" choice. Only set this for sections that actually
   * implement collapsing — otherwise the control would do nothing.
   *
   * The choice is TENANT-WIDE, and each collapsible section needs its OWN
   * server flag on `LeadFormConfig` (today: `basicDetails` →
   * `leadBasicDetailsExpanded`). Marking a second section collapsible without
   * adding its flag leaves a control that silently reads/writes the first
   * section's value — see `isExpanded` in ViewLeadFieldsSection.
   */
  collapsible?: boolean;
}

/**
 * Everything the View Lead drawer's Overview tab renders, in display order.
 * The settings panel builds its tick list from this, and the drawer checks
 * the same keys — so a field can't be offered here that the drawer ignores.
 * Preference custom fields are dynamic per tenant and tracked separately in
 * `customFields` (keyed by customInputId).
 */
export const VIEW_LEAD_FIELD_GROUPS: ViewLeadFieldGroup[] = [
  { key: "priority", label: "Priority Selector", fields: [] },
  {
    key: "basicDetails",
    label: "Basic Details",
    collapsible: true,
    fields: [
      { key: "basic.mobile", label: "Mobile" },
      { key: "basic.email", label: "Email" },
      { key: "basic.whatsapp", label: "WhatsApp" },
      { key: "basic.gender", label: "Gender" },
      { key: "basic.location", label: "Location" },
      { key: "basic.branch", label: "Branch" },
    ],
  },
  { key: "preference", label: "Preference", fields: [] },
  { key: "modules", label: "Module Profiles", fields: [] },
  { key: "dealAttachments", label: "Deal Module Attachments", fields: [] },
  { key: "stageStatus", label: "Stage / Status", fields: [] },
  {
    key: "leadSource",
    label: "Lead Source & Assignment",
    fields: [
      { key: "source.source", label: "Source" },
      { key: "source.campaign", label: "Campaign" },
      { key: "source.adset", label: "Ad set" },
      { key: "source.ad", label: "Ad (creative)" },
      { key: "source.reenquired", label: "Re-enquired via" },
      { key: "source.lastUpdated", label: "Last Updated" },
      { key: "source.dealCreated", label: "Deal Created" },
      { key: "source.leadCreated", label: "Lead Created" },
      { key: "source.assignedTo", label: "Assigned to" },
    ],
  },
  { key: "notes", label: "Notes", fields: [] },
];

/* ================= STATE ================= */

/**
 * Show/hide configuration for the View Lead drawer's Overview tab. Mirrors
 * the lead-card show/hide pattern: keys absent from a map default to VISIBLE,
 * so newly added fields (and new tenant custom fields) show up without a
 * migration. Persisted via redux-persist. Hiding is display-only — edit mode
 * still offers every field so mandatory ones can always be filled.
 */
export interface ViewLeadDisplayState {
  /** Static field/section keys from VIEW_LEAD_FIELD_GROUPS. Absent = shown. */
  fields: Record<string, boolean>;
  /** Preference custom fields, keyed by stringified customInputId. Absent = shown. */
  customFields: Record<string, boolean>;
  // A collapsible section's OPEN/CLOSED default used to live here as
  // `expandedByDefault`. It moved to the server (`LeadFormConfig
  // .leadBasicDetailsExpanded`) because the tenant wants one drawer layout for
  // the whole team, not a per-browser choice. Stale persisted blobs may still
  // carry the old key; it is simply ignored.
}

const initialState: ViewLeadDisplayState = {
  fields: {},
  customFields: {},
};

const viewLeadDisplaySlice = createSlice({
  name: "viewLeadDisplay",
  initialState,
  reducers: {
    setViewLeadFieldVisible(
      state,
      action: PayloadAction<{ key: string; visible: boolean }>
    ) {
      state.fields[action.payload.key] = action.payload.visible;
    },
    setViewLeadCustomFieldVisible(
      state,
      action: PayloadAction<{ id: string | number; visible: boolean }>
    ) {
      state.customFields[String(action.payload.id)] = action.payload.visible;
    },
    resetViewLeadDisplay() {
      return initialState;
    },
  },
});

export const {
  setViewLeadFieldVisible,
  setViewLeadCustomFieldVisible,
  resetViewLeadDisplay,
} = viewLeadDisplaySlice.actions;

export default viewLeadDisplaySlice.reducer;
